import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddComponent } from 'src/components/add/add.component';
import { Entry } from 'src/services/entry/Entry';
import { EntryService } from 'src/services/entry/entry.service';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { TagService } from 'src/services/tag/tag.service';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { TagSelectionService } from '../../services/tag-selection/tag-selection.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { TagSelectorBottomSheet } from '../tag-selector-bottom-sheet/tag-selector-bottom-sheet.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { trackByTagId } from 'src/utils/trackByTagId';
import { PrefundCurrencyPipe } from 'src/app/prefund-currency.pipe';
import { PeriodService } from 'src/app/services/period.service';
import { Tag } from 'functions/src/models/Tag';

@Component({
  selector: 'mobile-adder',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatBottomSheetModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    PrefundCurrencyPipe,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './mobile-adder.component.html',
  styleUrl: './mobile-adder.component.scss',
})
export class MobileAdderComponent {
  @Input()
  detailedTags: DetailedTag[] = [];

  @Input()
  tag: string = '';

  @Output()
  onHamburgerClick = new EventEmitter<void>();

  @Output()
  onSubmit = new EventEmitter<void>();

  @ViewChild('dateInput') dateInputElementRef: ElementRef | null = null;

  public memo: string | null = null;

  public isNegative = true;

  trackByTagId = trackByTagId;

  // @ts-ignore
  sheet: TagSelectorBottomSheet;
  amountFormControl = new FormControl<string>('');
  dateFormControl = new FormControl(new Date());
  entry: Entry & { tagFullPaths: string[] } = {
    amount: 0,
    date: new Date(),
    memo: '',
    tagIds: [],
    tagFullPaths: [],
  };

  private _bottomSheet = inject(MatBottomSheet);

  constructor(
    private tagService: TagService,
    private entryService: EntryService,
    private _matSnackBar: MatSnackBar,
    public tagSelectionService: TagSelectionService,
    private periodService: PeriodService
  ) {
    this.amountFormControl.valueChanges.subscribe((value) => {
      if (value !== null) {
        this.entry.amount = Number(value);
      }
    });
  }

  openBottomSheet(): void {
    this._bottomSheet.open(TagSelectorBottomSheet, {
      data: {
        detailedTags: this.detailedTags,
      },
    });
  }

  pickDate() {
    this.dateInputElementRef?.nativeElement.click();
  }

  type(value: number) {
    const currentValue = this.amountFormControl.value;
    if (!currentValue || currentValue === '0') {
      this.amountFormControl.setValue(value.toString());
    } else {
      this.amountFormControl.setValue(
        this.amountFormControl?.value!.toString() + value.toString()
      );
    }
  }

  backspace() {
    if (this.amountFormControl?.value!.toString().length > 0) {
      this.amountFormControl.setValue(
        this.amountFormControl?.value!.toString().slice(0, -1)
      );
    }
  }

  hasAmount() {
    return Number(this.amountFormControl.value) > 0;
  }

  addMemo() {
    this.memo = prompt('Add a note');
  }

  async submit() {
    if (this.amountFormControl.value) {
      await AddComponent.saveEntry(
        {
          amount:
            Number(this.amountFormControl.value) * (this.isNegative ? -1 : 1),
          date: this.dateFormControl.value ?? new Date(),
          memo: this.memo ?? '',
          tagIds: this.tagSelectionService.selectedTags
            .map((t) => t.id)
            .filter((id) => id !== undefined) as string[],
          tagFullPaths: this.tagSelectionService.selectedTags.map(
            (t) => t.fullPath
          ),
        },
        this.detailedTags,
        this.tagService,
        this.entryService,
        this._matSnackBar
      );
      this.clear();
      this.onSubmit.emit();
    }
  }

  decimal() {
    // Add a decimal point if it doesn't already contain one
    if (!this.amountFormControl.value?.includes('.')) {
      this.amountFormControl.setValue(this.amountFormControl.value + '.');
    }
  }

  clear() {
    this.memo = null;
    this.amountFormControl.setValue(null);
    this.tagSelectionService.selectedTags = [];
    this.isNegative = true;
    const targetTag = this.detailedTags.find((t) => t.fullPath === this.tag);

    const date = this.periodService.getDefaultDateForEntry(
      targetTag,
      this.detailedTags
    );
    this.dateFormControl.setValue(date);
  }

  getAsNumber(value: string | null): number | undefined {
    if (value) {
      return Number(value);
    } else {
      return undefined;
    }
  }
}
